/**
 * #2133 デザイン: 画面上部に出す会社名・スタジオ名を読む。
 * 既存の fetchCompany/fetchStudio(companies/{companyId} と
 * companies/{companyId}/studios/{studioId} の get)を使うだけで、読み取り
 * 範囲は各ロールが既に読めるドキュメントに限られる(firestore.rules の
 * belongsToCompanyPath)。
 *
 * 名前は表示のためだけのものなので、読めない・存在しない・失敗した場合は
 * null を返して表示しない(画面全体を止めない)。
 */
import { useEffect, useState } from 'react';
import { fetchCompany } from '@/lib/firebase/companies';
import { fetchStudio } from '@/lib/firebase/studios';
import { logger } from '@/lib/logger';

interface TenantNames {
  loading: boolean;
  companyName: string | null;
  studioName: string | null;
}

interface LoadedNames {
  key: string;
  companyName: string | null;
  studioName: string | null;
}

function nameOf(result: PromiseSettledResult<{ name: string } | null>): string | null {
  if (result.status === 'rejected') {
    logger.warn('Failed to load tenant name', { reason: String(result.reason) });
    return null;
  }
  const name = result.value?.name.trim() ?? '';
  return name === '' ? null : name;
}

export function useTenantNames(companyId: string | null, studioId: string | null): TenantNames {
  const key = `${companyId ?? ''}/${studioId ?? ''}`;
  // 取得結果を key と一緒に持ち、companyId/studioId が変わった直後に
  // 前のテナントの名前を出さない(MatList.tsx と同じ考え方)。
  const [loaded, setLoaded] = useState<LoadedNames | null>(null);

  useEffect(() => {
    if (!companyId) {
      return;
    }
    let active = true;
    void Promise.allSettled([
      fetchCompany(companyId),
      studioId ? fetchStudio(companyId, studioId) : Promise.resolve(null),
    ]).then(([company, studio]) => {
      if (!active) return;
      setLoaded({ key, companyName: nameOf(company), studioName: nameOf(studio) });
    });
    return () => {
      active = false;
    };
  }, [companyId, studioId, key]);

  if (!companyId) {
    return { loading: false, companyName: null, studioName: null };
  }
  const current = loaded?.key === key ? loaded : null;
  return {
    loading: current === null,
    companyName: current?.companyName ?? null,
    studioName: current?.studioName ?? null,
  };
}
